import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { StreamrAppService } from '../../services/streamrapp/StreamrAppService';
const MAX_VISIBLE = 4;
const pulse = keyframes `
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
`;
const OverlayRoot = styled.div `
    position: absolute;
    bottom: 80px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    pointer-events: none;
    z-index: 5;
`;
const ButtonRow = styled.div `
    display: flex;
    gap: 6px;
    flex-wrap: nowrap;
    pointer-events: auto;
    padding: 0 12px;
`;
const ParticipantPill = styled.button `
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border: none;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    backdrop-filter: blur(8px);
    transition: background 0.15s;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
        background: rgba(0, 0, 0, 0.7);
    }

    ${({ $isSpeaking }) => $isSpeaking &&
    css `
            outline: 2px solid rgba(76, 175, 80, 0.8);
            outline-offset: -2px;
        `}
`;
const SpeakingDot = styled.span `
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #4caf50;
    flex-shrink: 0;
    animation: ${pulse} 1.2s ease-in-out infinite;
`;
const MorePill = styled.button `
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border: none;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    backdrop-filter: blur(8px);
    transition: background 0.15s;

    &:hover {
        background: rgba(0, 0, 0, 0.7);
    }
`;
const ExpandedPanel = styled.div `
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 8px;
    background: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(12px);
    border-radius: 12px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 240px;
    overflow-y: auto;
    pointer-events: auto;
    min-width: 160px;
`;
const ExpandedItem = styled.button `
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: background 0.15s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }

    ${({ $isSpeaking }) => $isSpeaking &&
    css `
            background: rgba(76, 175, 80, 0.15);
        `}
`;
export function ParticipantButtonOverlay({ visible = true, }) {
    const lowEndActive = StreamrAppService.getOutput().useLowEndConferencingActive();
    const participants = StreamrAppService.getOutput().useLowEndParticipants();
    const dominantSpeakerUserId = StreamrAppService.getOutput().useDominantSpeakerUserId();
    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = useCallback(() => {
        setExpanded((prev) => !prev);
    }, []);
    if (!lowEndActive || !visible)
        return null;
    const nonDominant = participants.filter((p) => p.userId !== dominantSpeakerUserId);
    if (nonDominant.length === 0)
        return null;
    const visibleParticipants = nonDominant.slice(0, MAX_VISIBLE);
    const overflowCount = nonDominant.length - MAX_VISIBLE;
    return (_jsx(OverlayRoot, { children: _jsxs(ButtonRow, { children: [visibleParticipants.map((p) => (_jsx(ParticipantButton, { participant: p }, p.userId))), overflowCount > 0 && (_jsxs("div", { style: { position: 'relative' }, children: [_jsxs(MorePill, { onClick: toggleExpanded, children: ["+", overflowCount, " more"] }), expanded && (_jsx(ExpandedPanel, { children: nonDominant.slice(MAX_VISIBLE).map((p) => (_jsxs(ExpandedItem, { "$isSpeaking": p.isSpeaking, children: [p.isSpeaking && _jsx(SpeakingDot, {}), p.displayName] }, p.userId))) }))] }))] }) }));
}
function ParticipantButton({ participant, }) {
    return (_jsxs(ParticipantPill, { "$isSpeaking": participant.isSpeaking, children: [participant.isSpeaking && _jsx(SpeakingDot, {}), participant.displayName] }));
}
